import { get, set } from 'idb-keyval'
import { getOrCreateTerminalId, getPrintAgentConfig } from '@/lib/stationPrinterMap'

/**
 * The identifier an offline kitchen slip carries (F-CTRL-N52; readable form
 * F-CTRL-N61).
 *
 * WHY THIS REPLACED RESERVED ORDER NUMBERS
 *
 * Until now a terminal pre-reserved a block of real order numbers while online,
 * so an offline slip could print the number the order would keep for life. That
 * bought one genuine property — slip and POS never disagree — at a price that
 * turned out to be the whole feature:
 *
 *   - Reserving from the head of the day's sequence burnt 20 numbers per till
 *     per day, permanently, and a gap in a filed invoice sequence is what a tax
 *     authority reads as a missing filing (F-CTRL-N46, 2026-08-18).
 *   - Moving the block to a 9001+ band fixed the gaps but kept the mechanism,
 *     and with it a precondition: this terminal must have successfully talked to
 *     the server earlier the same business day, and must still hold numbers
 *     keyed to a business date it can only learn from the server. Both halves
 *     fail exactly when they matter. On 2026-08-26 a live till held twenty
 *     perfectly good numbers and refused to sell, because the date they were
 *     filed under could not be read while offline.
 *
 * So the number is now minted by the server when the order actually arrives, and
 * the slip carries this instead: a token that is obviously NOT an order number,
 * minted locally, needing nothing.
 *
 * Two properties matter and neither requires the server:
 *
 *   - It cannot be mistaken for an order number. "OFF-C1-007" next to "027"
 *     reads as what it is. A number that merely looked unusual (9001) did not —
 *     staff and reports both took it for a real one.
 *   - It is unique enough. The terminal prefix separates two tills that are dark
 *     simultaneously; the counter separates rounds on one till. The prefix is
 *     the till's own name where it has one ("Counter 1" -> "C1"), because a tag
 *     nobody recognises makes the slip harder to match, not easier. It is not
 *     globally unique and does not need to be: nothing keys off it, and the
 *     order it becomes carries it purely so a slip in the pass can be matched to
 *     a bill. That is why orders.offline_ticket_ref is deliberately not UNIQUE.
 *
 * The counter is per business day only in the loosest sense — it resets when it
 * grows past a day's plausible volume rather than on a date, because a date is
 * the thing an offline terminal cannot be sure of. Getting "OFF-A3-7" twice on
 * consecutive days is harmless; refusing to sell because the date looked wrong
 * is not, and that is the bug this file exists to delete.
 */

const SEQ_KEY = 'bhookly-offline-ticket-seq-v1'

/**
 * Wrap well below any real day's offline volume, so the number stays short on
 * a 32-column thermal slip. The busiest observed tenant-day is 242 orders in
 * total; an outage is a slice of that.
 */
export const TICKET_SEQ_WRAP = 999

/**
 * Zero-pad the counter to the width of the wrap, so tickets from one till read
 * and sort as a run: OFF-C1-001, OFF-C1-002, … OFF-C1-010. Unpadded, "OFF-C1-9"
 * sorts after "OFF-C1-10" everywhere a human or a spreadsheet looks at them,
 * which is the whole reason the ticket carries a number.
 */
const SEQ_PAD = 3

/**
 * Compress a terminal name into a tag short enough for a 32-column slip.
 *
 * "Counter 1" -> "C1", "Till 3" -> "T3", "Main Counter" -> "MAIN". A word plus
 * a number is how tills are actually labelled, and it is the only shape worth
 * compressing: the initial carries the word, the digits carry the till.
 *
 * Exported so the settings screen can show an operator the tag their slips will
 * carry, rather than making them fire an offline order to find out.
 */
export function tagFromTerminalName(name: string | null | undefined): string {
  const s = (name ?? '').trim().toUpperCase()
  if (!s) return ''
  const m = s.match(/^([A-Z])[A-Z]*\s*0*(\d{1,2})$/)
  if (m) return `${m[1]}${m[2]}`
  return s.replace(/[^A-Z0-9]/g, '').slice(0, 4)
}

/**
 * Short, readable, and stable for the life of the machine.
 *
 * Prefers the terminal's own name, so a slip in the pass names a till the staff
 * recognise. The uuid-derived tag it falls back to is unique but says nothing to
 * a human — "OFF-D978-4" does not tell a manager which till fired the food,
 * which is most of what the ticket is printed for.
 *
 * An unnamed terminal keeps the old tag rather than losing the property that
 * matters: two tills dark at the same time must not mint the same ticket.
 */
export function terminalTag(): string {
  const named = tagFromTerminalName(getPrintAgentConfig().terminalName)
  if (named) return named
  // Uppercase hex/uuid characters read cleanly on a thermal printer at 32
  // columns; four is enough to separate the two-or-three tills a café runs.
  return getOrCreateTerminalId().replace(/-/g, '').slice(0, 4).toUpperCase() || 'TILL'
}

/**
 * Mint the next ticket for this terminal.
 *
 * Deliberately has no failure mode. Everything it needs is local, so unlike the
 * reservation it replaces there is no "not ready" state to report and no way for
 * a cashier to be told they cannot take an order.
 */
export async function nextTicketRef(): Promise<string> {
  const prev = (await get<number>(SEQ_KEY)) ?? 0
  const next = prev >= TICKET_SEQ_WRAP ? 1 : prev + 1
  await set(SEQ_KEY, next)
  return `OFF-${terminalTag()}-${String(next).padStart(SEQ_PAD, '0')}`
}

/**
 * True for a string this module minted.
 *
 * The one place it matters: a ticket must never be sent to the server as an
 * order number. Order numbers are YYYYMMDD-NNN and are allocated server-side;
 * anything shaped like a ticket is ours and belongs in ticket_ref.
 */
export function isTicketRef(s: string | null | undefined): boolean {
  return typeof s === 'string' && s.startsWith('OFF-')
}

/** Test seam — resets the local counter. */
export async function __resetTicketSeqForTests(): Promise<void> {
  await set(SEQ_KEY, 0)
}
